import { registerIconLibrary } from "@awesome.me/webawesome"

// Register Lucide as a Web Awesome icon library so `<wa-icon library="lucide">`
// renders Lucide glyphs — the same icon set used elsewhere via `lucide-react`.
// Web Awesome components are framework-agnostic, so using `<wa-icon>` (rather
// than a React icon component) keeps icons portable across the Inertia → Turbo
// move. SVGs are fetched from jsDelivr's pinned `lucide-static` package.
//
// Imported as a side effect from both entrypoints. The creator dashboard
// (Inertia) already renders `<wa-icon>`; the public Turbo app doesn't yet,
// but registering on both keeps the wiring symmetrical so a future WA
// primitive on the listener side works without an entrypoint change. The
// cost is just the icon-library lookup map — no component definitions.
const LUCIDE_LIBRARY = {
  resolver: (name: string) =>
    `https://cdn.jsdelivr.net/npm/lucide-static@1.16.0/icons/${name}.svg`,
}

registerIconLibrary("lucide", LUCIDE_LIBRARY)

// The Turbo layouts (marketing, public, listener) additionally load Web
// Awesome via the kit script (kit.webawesome.com), whose autoloader defines
// `<wa-icon>` from its own CDN-hosted module graph. The icon-library registry
// is module-scoped, so the registration above only reaches the npm copy — the
// kit-defined element resolves against the kit copy's registry and renders
// lucide icons as empty placeholders. Import the kit's own icon-library
// module (the browser module cache guarantees the same instance its
// components use) and register lucide there too. Registration re-renders any
// already-connected icons, so ordering against the autoloader is safe.
interface WebAwesomeKitConfig {
  token: string
  kit_assets_base_url: string
  product_version: string
}

const kitConfig = (
  window as { WebAwesomeKitConfig?: WebAwesomeKitConfig } & Window
).WebAwesomeKitConfig

if (kitConfig) {
  const kitLibraryModuleUrl = `${kitConfig.kit_assets_base_url}/kit/${kitConfig.token}/webawesome@${kitConfig.product_version}/components/icon/library.js`
  import(/* @vite-ignore */ kitLibraryModuleUrl)
    .then((kit: { registerIconLibrary: typeof registerIconLibrary }) => {
      kit.registerIconLibrary("lucide", LUCIDE_LIBRARY)
    })
    .catch(() => {
      // Kit assets unreachable — nothing to register against; the npm-copy
      // registration above still covers layouts without the kit script.
    })
}

// Radix Dialog uses `react-remove-scroll` to scroll-lock the body while a
// dialog is open. Its wheel handler walks the event path looking for a
// scrollable ancestor inside the locked region, and `preventDefault`s if it
// doesn't find one. The walker cannot pierce shadow DOM, so for a wheel
// gesture over a `<wa-textarea>` it sees only the host element (overflow:
// visible) and the dialog content above — not the scrollable native
// `<textarea>` inside the component's shadow root. The event gets cancelled
// and trackpad/wheel scrolling silently fails (keyboard still works).
//
// Stop propagation at the capture phase before Radix's handler runs, but
// only when the wa-textarea actually has overflowing content. Chromium's
// compositor handles the native scroll itself and *does* pierce shadow DOM.
document.addEventListener(
  "wheel",
  (event) => {
    const host = event
      .composedPath()
      .find(
        (node): node is HTMLElement =>
          node instanceof HTMLElement && node.tagName === "WA-TEXTAREA",
      )
    if (!host) return
    const inner = host.shadowRoot?.querySelector("textarea")
    if (!inner || inner.scrollHeight <= inner.clientHeight) return
    event.stopPropagation()
  },
  { capture: true, passive: true },
)
